import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { GetLinkedRisksByInternalAuditIdResponse } from '@risksmart-app/trpc/src/types';
import type { GetLinkedRisksByInternalAuditIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

const mapTrpcLinkedItemsToGraphQL = (
  trpcData: GetLinkedRisksByInternalAuditIdResponse[]
): GetLinkedRisksByInternalAuditIdQuery => {
  return {
    linked_risks: trpcData.map((item) => {
      return { Id: item.Id, risk: item.target_risk };
    }),
  };
};

export const useGetLinkedRisksByInternalAuditId = (
  internalAuditId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.linkedItem.linkedRisksByInternalAuditId.queryOptions({
      internalAuditId,
    }),
    enabled: trpcEnabled && !skip, // Only enable when TRPC flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData ? mapTrpcLinkedItemsToGraphQL(trpcData) : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
